import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { STATUS_STYLES, shortDate, money } from "@/lib/api";
import { ArrowLeft, Building2, MapPin, Mail, Phone, User, TrendingUp, Truck } from "lucide-react";
import { SalesComments } from "@/components/SalesComments";
import FloorDisplay from "@/components/FloorDisplay";
import POSRequestModal from "@/components/POSRequestModal";
import AssignAgentModal from "@/components/AssignAgentModal";

export default function CustomerDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [visits, setVisits] = useState([]);
  const [sales, setSales] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [posOpen, setPosOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);

  useEffect(() => {
    api.get(`/customers/${id}`).then(r => setC(r.data));
    api.get(`/visits`, { params: { customer_id: id } }).then(r => setVisits(r.data));
    api.get(`/sales`, { params: { customer_id: id } }).then(r => setSales(r.data));
    api.get(`/photos`, { params: { customer_id: id } }).then(r => setPhotos(r.data));
    api.get(`/tasks`, { params: { customer_id: id } }).then(r => setTasks(r.data));
  }, [id]);

  if (!c) return <div>Loading…</div>;

  // Monthly sales history
  const byMonth = {};
  sales.forEach(s => {
    if (!s.invoice_date) return;
    const m = s.invoice_date.slice(0, 7);
    byMonth[m] = (byMonth[m] || 0) + (s.net_sales_value || 0);
  });
  const monthly = Object.entries(byMonth).sort().slice(-12);

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <Link to="/customers" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#003DA5]">
        <ArrowLeft className="w-4 h-4" /> Back to customers
      </Link>

      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{c.account_number}</div>
            <h1 className="text-3xl font-heading font-black">{c.name}</h1>
            <div className="text-slate-600 mt-1">{c.store_name}</div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <span className={`h-fit px-3 py-1 rounded-full text-xs font-semibold ${STATUS_STYLES[c.status] || "badge-inactive"}`}>{c.status}</span>
            <Link to={`/accounts/${c.id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-[#003DA5] hover:underline" data-testid="view-account-performance-btn">
              <TrendingUp className="w-4 h-4" /> View Account Performance
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-6 text-sm">
          <Info icon={MapPin} label="Address" value={`${c.address || ""} ${c.town || ""}, ${c.state || ""} ${c.postcode || ""}`} />
          <Info icon={User} label="Contact" value={c.contact_person} />
          <Info icon={Mail} label="Email" value={c.email} />
          <Info icon={Phone} label="Phone" value={c.phone} />
          <Info icon={Building2} label="Customer Type" value={c.customer_type} />
          <Info icon={User} label="First Order" value={shortDate(c.first_order_date)} />
          <Info icon={User} label="Last Order" value={shortDate(c.last_order_date)} />
          <Info icon={User} label="Last Visit" value={shortDate(c.last_visit_date)} />
        </div>
      </div>

      <FloorDisplay customerId={c.id} />

      {c && (
        <div className="flex justify-end gap-2 flex-wrap">
          <button onClick={() => setAssignOpen(true)} className="h-11 px-5 border-2 border-slate-300 text-slate-700 rounded-lg font-semibold text-sm flex items-center gap-2 hover:border-[#003DA5] hover:text-[#003DA5]" data-testid="assign-agent-btn">
            <User className="w-4 h-4" /> Assign Agent
          </button>
          <button onClick={() => setPosOpen(true)} className="h-11 px-5 border-2 border-[#003DA5] text-[#003DA5] rounded-lg font-semibold text-sm flex items-center gap-2 hover:bg-[#003DA5] hover:text-white transition" data-testid="request-pos-btn">
            <Truck className="w-4 h-4" /> Request POS Materials
          </button>
        </div>
      )}
      {posOpen && <POSRequestModal customerId={c.id} onClose={() => setPosOpen(false)} />}
      {assignOpen && <AssignAgentModal customer={c} onClose={() => setAssignOpen(false)} onSaved={() => { setAssignOpen(false); api.get(`/customers/${id}`).then(r => setC(r.data)); }} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Monthly Sales History">
          {monthly.length === 0 ? <div className="text-sm text-slate-500">No sales.</div> :
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500"><tr><th className="text-left py-2">Month</th><th className="text-right py-2">Net Sales</th></tr></thead>
              <tbody>{monthly.map(([m, v]) => <tr key={m} className="border-t"><td className="py-2">{m}</td><td className="py-2 text-right font-semibold">{money(v)}</td></tr>)}</tbody>
            </table>
          }
          <SalesComments customerId={c.id} month={monthly.length > 0 ? monthly[monthly.length - 1][0] : undefined} />
        </Section>

        <Section title={`Store Visits (${visits.length})`}>
          {visits.length === 0 ? <div className="text-sm text-slate-500">No visits yet.</div> :
            <ul className="divide-y divide-slate-100 text-sm">
              {visits.slice(0, 8).map(v => (
                <li key={v.id} className="py-2">
                  <div className="flex justify-between">
                    <span className="font-medium">{shortDate(v.visit_date)}</span>
                    <span className="text-xs text-slate-500">{v.agent_name}</span>
                  </div>
                  <div className="text-xs text-slate-600">{v.visit_purpose}</div>
                </li>
              ))}
            </ul>
          }
        </Section>

        <Section title={`Order History (${sales.length})`}>
          {sales.length === 0 ? <div className="text-sm text-slate-500">No orders.</div> :
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-500 uppercase text-[10px]"><tr><th className="text-left py-1">Date</th><th className="text-left">Invoice</th><th className="text-left">Product</th><th className="text-right">Net</th></tr></thead>
                <tbody>
                  {sales.slice(0, 30).map(s => (
                    <tr key={s.id} className="border-t"><td className="py-1.5">{shortDate(s.invoice_date)}</td><td>{s.invoice_number}</td><td>{s.product_name}</td><td className="text-right">{money(s.net_sales_value)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          }
        </Section>

        <Section title={`Store Photos (${photos.length})`}>
          {photos.length === 0 ? <div className="text-sm text-slate-500">No photos.</div> :
            <div className="grid grid-cols-3 gap-2">{photos.slice(0, 9).map(p => <Thumb key={p.id} p={p} />)}</div>
          }
        </Section>

        <Section title={`Tasks (${tasks.length})`}>
          {tasks.length === 0 ? <div className="text-sm text-slate-500">No tasks.</div> :
            <ul className="divide-y divide-slate-100 text-sm">
              {tasks.map(t => (
                <li key={t.id} className="py-2 flex justify-between">
                  <div>
                    <div className="font-medium">{t.title}</div>
                    <div className="text-xs text-slate-500">Due {shortDate(t.due_date)}</div>
                  </div>
                  <span className="text-xs px-2 py-1 rounded-full badge-info">{t.status}</span>
                </li>
              ))}
            </ul>
          }
        </Section>

        <Section title="Notes">
          <div className="text-sm text-slate-600 whitespace-pre-wrap">{c.notes || "—"}</div>
        </Section>
      </div>
    </div>
  );
}

const Info = ({ icon: Icon, label, value }) => (
  <div className="flex gap-3">
    <Icon className="w-4 h-4 text-slate-400 mt-0.5" />
    <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div><div>{value || "—"}</div></div>
  </div>
);
const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <h3 className="text-lg font-heading font-semibold mb-3">{title}</h3>{children}
  </div>
);
function Thumb({ p }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancel = false;
    api.get(`/photos/${p.id}/download`, { responseType: "blob" }).then(r => { if (!cancel) setUrl(URL.createObjectURL(r.data)); });
    return () => { cancel = true; };
  }, [p.id]);
  return <div className="aspect-square bg-slate-100 rounded overflow-hidden">{url && <img src={url} className="w-full h-full object-cover" alt={p.category} />}</div>;
}
